import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type { MutableRefObject } from 'react'
import { printFrameCorners, zoomToFitFrame } from '../../lib/printFrame'
import type { Size } from '../../lib/printLayout'
import type { PaperFormat, PrintOrientation } from '../../lib/printPaper'
import { loadPrintProjector, type PrintCrs, type PrintProjector } from '../../lib/printProjection'
import { printCrsOf, type PrintProbe, type PrintProbeLookup } from '../../lib/printProbe'
import type { GeoPt } from '../../lib/types'
import type { MapProjectFn, MapUnprojectFn } from './MapProjectionProbe'
import type { PageMm } from '../../lib/printCustomSheet'
import { printProbeKey, usePrintProbe } from './usePrintProbe'
import { NO_PRINT_INSETS, type PrintInsets } from './usePrintInsets'

/** A camera move request: where to sit and how far out, plus the counter that
 *  makes each request distinct. A counter rather than a timestamp, because two
 *  fits inside the same millisecond (a scale change immediately followed by
 *  the Fit button) would otherwise collide and the second would be ignored. */
export interface PrintFrameFocus {
  lat: number
  lon: number
  zoom: number
  nonce: number
  padding?: PrintInsets
}

export interface UsePrintFrameArgs {
  /**
   * The custom sheet's millimetres, when the surface has one. Wins over
   * `format`/`orientation` in the corners, exactly as it does everywhere else.
   */
  page?: PageMm
  /**
   * True while the sheet is being DERIVED from the camera — a custom page size
   * or scale that follows zoom.
   *
   * It suppresses the fit that a sheet change normally triggers, and that
   * suppression is the whole reason the option exists: the caller computed this
   * sheet FROM the viewport, so fitting the map to it would move the camera,
   * which would derive a new sheet, which would fit again. The sheet is still
   * recorded as fitted, so lifting this later does not fire a fit for a sheet
   * the user already composed by hand.
   */
  sheetFollowsCamera?: boolean
  /**
   * Zoom OUT to the sheet, once per bump, even though the sheet follows the
   * camera.
   *
   * A page derived from the viewport is normally at most the viewport, and then
   * the caller never bumps this. It cannot always be: the smallest sheet the
   * server will print is 100 mm, which at 1:50 000 — the scale a custom sheet
   * starts on — is four kilometres of ground, so ANY closer view derives a
   * frame larger than the map it is drawn on. Left alone, the frame's edges sit
   * off screen with nothing shading the surround, and the panel's own advice to
   * compose inside the frame describes something the user cannot see.
   *
   * **The caller decides, and that is deliberate.** This hook could measure the
   * frame's corners against its own container, and the first version did —
   * which turned out to be a positive-feedback loop away from the projection's
   * central meridian, because the pixel measurement and the measurement the
   * page was derived from inflate in opposite directions (the full account is
   * on `pageExceedsViewport`, which is where the question is answered now).
   * Whoever sized the sheet knows whether the sheet could be made to fit; this
   * hook only knows pixels.
   *
   * It is also why this is a COUNTER rather than a condition. At the 100 mm
   * floor the overflow is permanent — the sheet has stopped shrinking with the
   * view — so a rule that fired on the condition would pull the camera out of
   * every attempt to zoom in, and again on a window resize. One bump, one
   * zoom-out.
   *
   * Both custom controls use it, because both have a floor they cannot derive
   * past: the page stops at 100 mm, and the scale series stops at 1:1000, so
   * either can end up describing more ground than the map is showing.
   */
  refitToken?: number
  /**
   * Whether a print surface is actually showing the frame right now. False
   * while the editor's print panel is closed: the page around it stays
   * mounted, and everything derived here — the region answer, the corners, the
   * record of what has already been fitted — describes a surface the user is
   * not looking at.
   */
  enabled: boolean
  /** The map's live centre, or null before it has reported one. */
  center: GeoPt | null
  /** The map's live zoom, or null before it has reported one. */
  zoom: number | null
  /** The map container's CSS pixel size, or null before it is measured. */
  containerPx: Size | null
  padding?: PrintInsets
  format: PaperFormat
  orientation: PrintOrientation
  scale: number
  /** The Estate-backed print probe; without it there is no frame at all,
   *  because the sheet is composed in the CRS the server names and guessing
   *  one would draw a rectangle the server will not print. */
  getPrintProbe?: PrintProbeLookup
}

export interface PrintFrame {
  /** The sheet's four corners in lon/lat, or null while there is nothing
   *  truthful to draw. */
  corners: GeoPt[] | null
  /**
   * The loaded projector for the sheet's CRS, or null while there is none —
   * before the region has answered, before proj4 has loaded, and whenever the
   * surface is not showing a frame.
   *
   * Exposed because the print payload has to be clipped and simplified in the
   * SAME projection the sheet is composed in; a caller that loaded its own
   * projector could clip against a different plane than the rectangle the user
   * was shown.
   */
  projector: PrintProjector | null
  /**
   * The CRS the sheet is composed in, or null while the region lookup has not
   * answered.
   *
   * The CRS itself rather than a boolean off it, because callers ask different
   * questions of it. Callers that need more than the CRS — which flavours
   * print here, whether the server prints here at all — read `probe`.
   */
  crs: PrintCrs | null
  /**
   * The probe answer for the live surface, or null while there is none — before
   * the lookup has answered, and whenever the surface is not showing a frame.
   * The print panel reads the flavour list off it (`baseMapBlocksPrint`).
   */
  probe: PrintProbe | null
  /**
   * True when the server has said nothing prints at this centre. No frame is
   * drawn; the panel says so and withholds Create. Distinct from "no answer
   * yet", which draws nothing and says nothing.
   */
  printUnavailable: boolean
  /** True until the live probe and its projector are ready. Failures keep
   * Create disabled and expose Retry instead of submitting an unclipped job. */
  projectorPending: boolean
  printError: 'probe' | 'projector' | null
  retry: () => void
  /** The latest fit request, to hand to the map's nonce-keyed `focus` prop. */
  focus: PrintFrameFocus | null
  /**
   * Fits the map to the frame. Returns whether a fit was actually issued —
   * false when something it needs (the corners, the map's projection, its size)
   * is not there yet.
   *
   * No surface calls this today: the print panel's "fit the frame" button was
   * withdrawn, and a custom sheet keeps itself in view through the two effects
   * below. It stays on the interface because it is what those effects are
   * written in terms of, and because a test can ask for a fit at a moment no
   * camera move would produce.
   */
  fitFrame: () => boolean
  /** Hand to `MapProjectionProbe`, rendered inside the map. */
  projectRef: MutableRefObject<MapProjectFn | null>
  /** Hand to `MapProjectionProbe`; read when deriving a sheet from the camera. */
  unprojectRef: MutableRefObject<MapUnprojectFn | null>
}

/**
 * The print sheet the server will compose, as map geometry, plus the camera
 * moves that keep it in view.
 *
 * Both print surfaces — `TeamMapPrintView` and the map editor's print mode —
 * need the same five-step chain: ask the print server (through Estate) what a
 * sheet centred here would use, lazily load a projector for the CRS it names,
 * build the sheet's corners in that projection around the live centre, and fit
 * the map to them. They had a copy each, and every defect in the chain had to
 * be found and fixed twice; this is the one implementation.
 *
 * Fits are issued on the first corners and on a change of sheet
 * (paper/orientation/scale) — deliberately NOT on every corners change, which
 * also happens on every pan: re-fitting there would fight the user's own zoom,
 * and leaving the fit behind until something asks for it back is the point.
 *
 * A sheet that FOLLOWS the camera turns both of those off, because the camera
 * is what produced it (`sheetFollowsCamera`), and gets one rule of its own
 * instead: a zoom-out owed to a sheet the map cannot hold, claimed by the
 * user's own change to the sheet rather than by any camera move
 * (`refitToken`).
 */
export function usePrintFrame({
  enabled,
  center,
  zoom,
  containerPx,
  padding = NO_PRINT_INSETS,
  format,
  orientation,
  scale,
  page,
  sheetFollowsCamera = false,
  refitToken = 0,
  getPrintProbe,
}: UsePrintFrameArgs): PrintFrame {
  // What the print server would use for a sheet centred here, decided by the
  // map server for the live centre — undefined until the first answer.
  const { probe: answered, key: answeredKey, error: probeFailed, retry: retryProbe } = usePrintProbe(center, getPrintProbe, enabled)
  const answerIsCurrent = center !== null && answeredKey === printProbeKey(center)
  const probe = enabled && answerIsCurrent && answered ? answered : null
  // Retain a stale frame for orientation, but never expose its projector for
  // submission. The current centre needs its own successful probe first.
  const crs = useMemo(() => printCrsOf(enabled ? answered : null), [enabled, answered])
  const printUnavailable = probe?.status === 'UNAVAILABLE'

  // proj4 is loaded on demand, so the projector arrives a tick after the CRS —
  // and the CRS it was loaded FOR is held with it. Two separate states let a
  // country change pair the new CRS with the previous country's projector for
  // one render, which is a rectangle drawn in a grid it was not computed in;
  // one state cannot.
  const [loaded, setLoaded] = useState<{ crs: PrintCrs; projector: PrintProjector } | null>(null)
  // Which CRS's load was REFUSED, so a caller waiting for a projector can be
  // told to stop waiting. Keyed on the CRS for the same reason `loaded` is: a
  // failure belongs to the country it was attempted for, and moving to another
  // country is a fresh attempt rather than an inherited failure.
  const [loadFailed, setLoadFailed] = useState<PrintCrs | null>(null)
  const [loadAttempt, setLoadAttempt] = useState(0)
  const retry = useCallback(() => {
    if (probeFailed || !answerIsCurrent) retryProbe()
    setLoadFailed(null)
    setLoadAttempt((attempt) => attempt + 1)
  }, [probeFailed, answerIsCurrent, retryProbe])
  useEffect(() => {
    setLoadFailed(null)
    if (!crs) {
      setLoaded(null)
      return
    }
    let cancelled = false
    void loadPrintProjector(crs)
      .then((projector) => {
        if (!cancelled) setLoaded({ crs, projector })
      })
      // swallowed: a failed load leaves `loaded` null, which is exactly "no
      // frame" — the surface keeps working with no rectangle on it rather than
      // taking the whole print view down with an unhandled rejection. The
      // realistic cause is not a bug but a redeploy: the hashed proj4 chunk
      // this build asks for is gone from the CDN while the tab stays open, and
      // the fetch 404s. `loadPrintProjector` evicts its cache entry on
      // rejection, so reopening the panel (a fresh effect run) retries.
      //
      // RECORDED as well as swallowed, though: a caller gating an action on
      // `projectorPending` has to be able to tell a load still in flight from
      // one that is never coming, and only this handler knows the difference.
      .catch(() => {
        if (!cancelled) setLoadFailed(crs)
      })
    return () => {
      cancelled = true
    }
  }, [crs, loadAttempt])

  // Depended on by millimetres rather than by object identity: the host rebuilds
  // the custom page on every render, so keying the memo on `page` itself would
  // recompute the corners — and hand out a fresh array — on every pan.
  const pageW = page?.w ?? null
  const pageH = page?.h ?? null

  // The sheet's four corners, or null while there is nothing truthful to draw.
  const corners = useMemo(() => {
    if (!enabled || !center || !crs || !loaded || loaded.crs !== crs) return null
    const sheet = pageW !== null && pageH !== null ? { w: pageW, h: pageH } : undefined
    return printFrameCorners(loaded.projector, { center, scale, format, orientation, page: sheet })
  }, [enabled, center, crs, loaded, scale, format, orientation, pageW, pageH])

  // The same readiness test `corners` makes, minus the centre: a projector is
  // usable for clipping as soon as it belongs to the current region's CRS,
  // whether or not the map has reported where it is looking.
  const projector = useMemo(
    () => (enabled && answerIsCurrent && probe?.status === 'SUCCESS' && crs && loaded && loaded.crs === crs ? loaded.projector : null),
    [enabled, answerIsCurrent, probe, crs, loaded],
  )

  // Probe and projector failures are distinct from pending preparation.
  const printError = enabled && probeFailed ? 'probe' : enabled && crs && loadFailed === crs ? 'projector' : null
  const projectorPending = enabled && !printUnavailable && printError === null && projector === null

  // The live map's screen projection, filled by `MapProjectionProbe` (the only
  // place `useMap()` resolves) so the fit below can measure the frame in pixels
  // exactly rather than approximating it from lon/lat spans.
  //
  // Wrapped in an accessor pair rather than handed out as a plain ref because
  // the auto-fit has to KNOW when the projection appears: a ref write triggers
  // no render, so corners that arrive before the map has reported its
  // projection would find nothing to measure and never be retried. The bump
  // costs one render per map lifetime — the projection function is written once
  // on mount and cleared on unmount — not one per pan.
  // Read by millimetre and pixel rather than by object identity, for the same
  // reason `page` is: a host rebuilding the size object each render would
  // otherwise re-enter every effect that measures the map.
  const pxW = containerPx ? Math.max(1, containerPx.w - padding.left - padding.right) : null
  const pxH = containerPx ? Math.max(1, containerPx.h - padding.top - padding.bottom) : null

  const projectFnRef = useRef<MapProjectFn | null>(null)
  const [probeGeneration, setProbeGeneration] = useState(0)
  const projectRef = useMemo<MutableRefObject<MapProjectFn | null>>(
    () => ({
      get current(): MapProjectFn | null {
        return projectFnRef.current
      },
      set current(fn: MapProjectFn | null) {
        if (projectFnRef.current === fn) return
        projectFnRef.current = fn
        setProbeGeneration((g) => g + 1)
      },
    }),
    [],
  )

  // A plain ref: it is read on demand when the camera stops, never by an effect
  // that must wake when it appears, so it needs none of `projectRef`'s
  // render-triggering machinery.
  const unprojectRef = useRef<MapUnprojectFn | null>(null)

  const [focus, setFocus] = useState<PrintFrameFocus | null>(null)
  const fitNonce = useRef(0)

  const fitFrame = useCallback((): boolean => {
    const project = projectFnRef.current
    if (!project || !corners || !center || zoom === null || !containerPx) return false
    const fitted = zoomToFitFrame({ corners, containerPx: { w: pxW!, h: pxH! }, currentZoom: zoom, project })
    fitNonce.current += 1
    // Centre unchanged: the fit is about how much ground is visible, and moving
    // the centre would recompose the sheet the user just framed.
    setFocus({ lat: center.lat, lon: center.lon, zoom: fitted, nonce: fitNonce.current, padding })
    return true
  }, [corners, center, zoom, containerPx, pxW, pxH, padding])

  // The page belongs in the key: two custom sheets differ by millimetres alone,
  // and without it a resize would not count as a change of sheet at all.
  const sheetKey = `${format}|${orientation}|${scale}|${pageW ?? ''}x${pageH ?? ''}|${pxW}x${pxH}|${padding.left}:${padding.right}`
  const liveKey = enabled && center ? printProbeKey(center) : null
  const fittedSheetRef = useRef<string | null>(null)
  useEffect(() => {
    if (!enabled) {
      // Forgotten on the way out, so the next open re-fits: "fit on opening the
      // surface" has to hold for a surface that opens more than once.
      fittedSheetRef.current = null
      return
    }
    if (!corners) return
    // Only fit to a frame whose country was answered for where the map IS. On
    // reopening the panel somewhere else, the pending answer may still change
    // the grid the corners are built in, and fitting to the interim rectangle
    // would move the camera twice for one open. ("No answer at all" needs no
    // clause of its own: without one there is no region, so no CRS and no
    // corners, and the guard above has already returned.)
    if (answeredKey !== liveKey) return
    if (fittedSheetRef.current === sheetKey) return
    // A sheet the CAMERA just produced is not one to fit back to — see
    // `sheetFollowsCamera`. Recorded as fitted so that turning the option off
    // later does not fire a fit for a sheet the user already composed.
    if (sheetFollowsCamera) {
      fittedSheetRef.current = sheetKey
      return
    }
    // Recorded only on a fit that actually happened. Recording the attempt
    // instead left the sheet marked as fitted when the map had not yet reported
    // its projection — the map then stayed wherever it was, and no later render
    // would try again.
    if (fitFrame()) fittedSheetRef.current = sheetKey
  }, [enabled, corners, sheetKey, fitFrame, answeredKey, liveKey, probeGeneration, sheetFollowsCamera])

  // The sheet that will not fit on the map — see `fitWhenSheetOverflows`. Its
  // own effect rather than a clause in the one above, because it has to run on
  // every corners change (a pan moves the sheet too) and not only when the
  // sheet key changes.
  // The last sheet gesture answered. One zoom-out per gesture, and none at all
  // for a camera move — which is what keeps this from becoming a loop, and what
  // leaves the hunter free to zoom in past a sheet that cannot shrink any
  // further.
  const answeredRefitRef = useRef(refitToken)
  useEffect(() => {
    if (!enabled) {
      // The next open starts owing nothing: whatever gesture went unanswered
      // was made against a surface the user has since left.
      answeredRefitRef.current = refitToken
      return
    }
    if (refitToken === answeredRefitRef.current) return
    // Not yet answerable rather than not owed: the sheet the gesture produced
    // has to exist before it can be fitted to, so the debt stands until the
    // derivation and the projector have both landed.
    const project = projectFnRef.current
    if (!project || !corners || !center || zoom === null || pxW === null || pxH === null) return
    if (answeredKey !== liveKey) return
    answeredRefitRef.current = refitToken
    const fitted = zoomToFitFrame({
      corners, containerPx: { w: pxW, h: pxH }, currentZoom: zoom, project,
    })
    // Only ever outward: a sheet smaller than the map is a sheet with room
    // around it, not a problem, and zooming in would fight the hunter's own
    // gesture. The twentieth of a zoom level of slack keeps rounding in the
    // derived page from making the map twitch on a camera tick.
    if (fitted >= zoom - 0.05) return
    fitNonce.current += 1
    setFocus({ lat: center.lat, lon: center.lon, zoom: fitted, nonce: fitNonce.current, padding })
  }, [enabled, refitToken, corners, center, zoom, pxW, pxH, padding, answeredKey, liveKey, probeGeneration])

  return {
    corners,
    projector,
    crs,
    probe,
    printUnavailable,
    projectorPending,
    printError,
    retry,
    focus,
    fitFrame,
    projectRef,
    unprojectRef,
  }
}
